import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Building2, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { parseOrders, parsePositions } from '@/stores/accountStore';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { describeMarket } from '@/lib/kalshiTicker';
import {
  sportFromTicker,
  sportMatchesFilter,
  cycleSportFilter,
  type SportFilter,
} from '@/lib/kalshiTicker/sportFilter';
import { SPORT_TABS, type Sport } from '@/views/ValueDashboard/types';
import {
  SummaryTile,
  SummaryStrip,
  ViewModeToggle,
  CardGrid,
  CompactListGrid,
} from '@/components/cards/cardKit';
import { fmtUsd, fmtUsdSigned, titleParts } from '@/components/cards/cardFormat';
import { PositionCard, CompactPositionItem } from '@/components/cards/PositionCard';
import { type PositionCardVM } from '@/components/cards/positionCardModel';
import { americanOdds } from '@/views/ValueDashboard/tradeModalBook';
import { type OrgTraderRow } from '@/components/organisms/OrgTradersTable';

// Same dense→table→card→mini choices as the Positions page. positionViewMode
// is a shared persisted preference, so the org view tracks whatever the trader
// last picked on /positions.
const POSITION_VIEW_MODES = ['compact', 'table', 'card', 'mini'] as const;

interface OrgDetailProps {
  /** Admin path: the org being inspected (sent as ?orgId). Omit for the
   *  manager "own org" path — the relay scopes to the caller's org. */
  orgId?: string;
  orgName?: string;
  /** Omit on the manager nav surface (no parent to go back to). */
  onBack?: () => void;
  onTraderClick: (row: OrgTraderRow) => void;
}

// One position on the org account, attributed to the trader who owns it
// (from /org/positions-by-trader — the relay's per-trader fills ledger).
interface OrgPositionRow {
  userId: string;
  ownerKind: 'trader' | 'bot' | 'unattributed';
  ownerLabel: string | null;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  avgCents: number;
  costBasisCents: number;
  sport: Sport | null;
}

// One resting order on the org account, tagged with its owning trader
// (from /org/orders-by-trader — null owner = unattributed, the failsafe truth).
interface OrgOrderRow {
  orderId: string;
  ownerKind: 'trader' | 'bot' | 'unattributed';
  ownerLabel: string | null;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  priceCents: number | null;
  sport: Sport | null;
}

interface OrgFillRow {
  fillId: string;
  orderId: string | null;
  ownerKind: 'trader' | 'bot' | 'unattributed';
  ownerLabel: string | null;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  qty: number;
  priceCents: number | null;
  feeCents: number | null;
  executedAt: string;
  sport: Sport | null;
}

function SportFilterChip({
  mode,
  label,
  onClick,
}: {
  mode: 'off' | 'include' | 'exclude';
  label: string;
  onClick: () => void;
}) {
  const cls =
    mode === 'include'
      ? 'border-primary/50 bg-primary/15 text-primary'
      : mode === 'exclude'
        ? 'border-red-500/50 bg-red-500/10 text-red-400 line-through'
        : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${cls}`}
    >
      {label}
    </button>
  );
}

function SideBadge({ side, betTeam }: { side: 'yes' | 'no'; betTeam: string | null }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
        side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {betTeam ? `(${betTeam}) ` : ''}
      {side.toUpperCase()}
    </span>
  );
}

function OwnerTag({ ownerKind, label }: { ownerKind: 'trader' | 'bot' | 'unattributed'; label: string | null }) {
  if (ownerKind === 'bot') {
    return (
      <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-400" title={label ?? 'Bot-owned order'}>
        {label ?? 'bot'}
      </span>
    );
  }
  if (ownerKind === 'unattributed') {
    return (
      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400" title="No relay ledger row — placed outside the app or before tracking. Still really on Kalshi.">
        unattributed
      </span>
    );
  }
  return <span className="text-foreground/90 text-xs">{label}</span>;
}

// Build a PositionCard VM from an attributed org row + the live mark, so the
// card / compact / mini views render identically to the Positions page. Read-
// only here (editable: false) — the admin/manager doesn't close traders'
// positions from this aggregate view.
function orgPositionVM(p: OrgPositionRow, nowCents: number | null): PositionCardVM {
  const { title, subtitle, team } = titleParts(p.ticker, p.side);
  // P&L (and cashOut below) off the EXACT ledger basis, not the whole-cent
  // rounded avg — matches usePositionPnl / OrgTradersTable (the I-035 fix), so
  // the card stays coherent (P&L === cashOut − cost). These are GROSS mark-to-
  // market: no closing-fee estimate, so they read a touch higher than each
  // trader's own /positions card, which nets an estimated taker close fee.
  const pnlCents = nowCents != null ? nowCents * p.qty - p.costBasisCents : null;
  return {
    key: `${p.userId}-${p.ticker}-${p.side}`,
    venue: 'kalshi',
    ticker: p.ticker,
    title,
    subtitle,
    eventStartLabel: null,
    side: p.side,
    team,
    qty: p.qty,
    pendingSellQty: 0,
    avgCents: p.avgCents,
    avgOdds: americanOdds(p.avgCents),
    feesCents: null,
    costDollars: p.costBasisCents / 100,
    nowCents,
    nowOdds: nowCents != null ? americanOdds(Math.round(nowCents)) : null,
    pnlDollars: pnlCents != null ? pnlCents / 100 : null,
    cashOutDollars: nowCents != null ? (nowCents * p.qty) / 100 : null,
    ifWinDollars: (p.qty * 100 - p.costBasisCents) / 100,
    grossPayoutDollars: p.qty,
    impliedOdds: p.qty > 0 ? americanOdds(p.costBasisCents / p.qty) : null,
    editable: false,
  };
}

export function OrgDetail({ orgId, orgName, onBack, onTraderClick }: OrgDetailProps) {
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [positions, setPositions] = useState<OrgPositionRow[]>([]);
  const [orders, setOrders] = useState<OrgOrderRow[]>([]);
  const [fills, setFills] = useState<OrgFillRow[]>([]);
  const [members, setMembers] = useState<OrgTraderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState<SportFilter>(() => new Map());

  // Live mark for the Now / unrealized columns. version drives re-render on
  // every hydra tick; getMarketPrice returns the YES mid (cents).
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);

  const orgQueryParam = orgId ? `?orgId=${orgId}` : '';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [posRes, ordRes, fillsRes, acctRes] = await Promise.all([
        apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`),
        apiFetch(`/api/manager/org/orders-by-trader${orgQueryParam}`),
        apiFetch(`/api/manager/org/fills-by-owner${orgQueryParam}`),
        apiFetch(`/api/manager/org/account${orgQueryParam}`),
      ]);
      const posData = (await posRes.json().catch(() => null)) as { rows?: OrgTraderRow[]; error?: string } | null;
      const ordData = (await ordRes.json().catch(() => null)) as
        | { orders?: Array<Record<string, unknown>>; error?: string }
        | null;
      const fillsData = (await fillsRes.json().catch(() => null)) as
        | { fills?: Array<Record<string, unknown>>; error?: string }
        | null;
      const acctData = (await acctRes.json().catch(() => null)) as
        | { balance?: Record<string, unknown>; positions?: unknown[]; error?: string }
        | null;

      if (!posRes.ok || !posData?.rows) {
        setError(
          typeof posData?.error === 'string'
            ? posData.error
            : 'Could not load org data. The org may not have a Kalshi credential configured.',
        );
        setPositions([]);
        setOrders([]);
        setFills([]);
        setBalanceCents(null);
        setLoading(false);
        return;
      }

      // Positions: flatten each trader's openPositions, tagging the owner.
      const posRows: OrgPositionRow[] = posData.rows.flatMap((r) =>
        (r.openPositions ?? []).map((p) => ({
          userId: r.userId,
          ownerKind: 'trader' as const,
          ownerLabel: r.username,
          ticker: p.ticker,
          side: p.side,
          qty: p.netCount,
          avgCents: p.avgEntryPriceCents,
          // Exact ledger cost basis when present, else avg × contracts.
          costBasisCents: p.costBasisCents ?? Math.round(p.avgEntryPriceCents * p.netCount),
          sport: sportFromTicker(p.ticker),
        })),
      );
      const attributedQty = new Map<string, number>();
      for (const p of posRows) {
        const key = `${p.ticker}::${p.side}`;
        attributedQty.set(key, (attributedQty.get(key) ?? 0) + p.qty);
      }
      const venuePositions = parsePositions({ positions: acctData?.positions ?? [] });
      const residualRows: OrgPositionRow[] = venuePositions.flatMap((p) => {
        const key = `${p.ticker}::${p.side}`;
        const residual = p.position - (attributedQty.get(key) ?? 0);
        if (residual <= 0.0001) return [];
        return [{
          userId: 'unattributed',
          ownerKind: 'unattributed' as const,
          ownerLabel: null,
          ticker: p.ticker,
          side: p.side,
          qty: residual,
          avgCents: p.averagePrice,
          costBasisCents: p.costBasisCents ?? Math.round(p.averagePrice * residual),
          sport: sportFromTicker(p.ticker),
        }];
      });
      setPositions([...posRows, ...residualRows]);
      setMembers(posData.rows);

      // Orders: parse the raw Kalshi orders with the canonical helper, then
      // re-attach the owner tag the relay added (parseOrders drops extras).
      const rawOrders = ordData?.orders ?? [];
      const ownerByOrderId = new Map<string, { kind: OrgOrderRow['ownerKind']; label: string | null }>();
      for (const o of rawOrders) {
        const id = o.order_id;
        if (typeof id === 'string') {
          const kind = o.ownerKind === 'bot' ? 'bot' : o.ownerKind === 'trader' ? 'trader' : 'unattributed';
          const label =
            kind === 'bot'
              ? ((o.botLabel as string | null) ?? (o.botAgentId as string | null) ?? 'bot')
              : ((o.ownerUsername as string | null) ?? null);
          ownerByOrderId.set(id, { kind, label });
        }
      }
      const parsed = parseOrders({ orders: rawOrders });
      const ordRows: OrgOrderRow[] = parsed.map((o) => {
        const owner = ownerByOrderId.get(o.orderId) ?? { kind: 'unattributed' as const, label: null };
        const sidePrice =
          o.side === 'no'
            ? (o.noPrice ?? (o.yesPrice != null ? 100 - o.yesPrice : null))
            : (o.yesPrice ?? (o.noPrice != null ? 100 - o.noPrice : null));
        return {
          orderId: o.orderId,
          ownerKind: owner.kind,
          ownerLabel: owner.label,
          ticker: o.ticker,
          side: o.side,
          qty: o.remainingCount,
          priceCents: sidePrice,
          sport: sportFromTicker(o.ticker),
        };
      });
      setOrders(ordRows);

      const fillRows: OrgFillRow[] = (fillsData?.fills ?? []).map((f) => {
        const ownerKind = f.ownerKind === 'bot' ? 'bot' : f.ownerKind === 'trader' ? 'trader' : 'unattributed';
        const side = f.side === 'no' ? 'no' : 'yes';
        const ticker = String(f.ticker ?? '');
        return {
          fillId: String(f.fill_id ?? f.trade_id ?? ''),
          orderId: typeof f.order_id === 'string' ? f.order_id : null,
          ownerKind,
          ownerLabel:
            ownerKind === 'bot'
              ? ((f.botLabel as string | null) ?? (f.botAgentId as string | null) ?? 'bot')
              : ((f.ownerUsername as string | null) ?? null),
          ticker,
          side,
          action: f.action === 'sell' ? 'sell' : 'buy',
          qty: Number(f.count ?? 0),
          priceCents: typeof f.price === 'number' ? f.price : null,
          feeCents: typeof f.fee_cents === 'number' ? f.fee_cents : null,
          executedAt: String(f.created_time ?? ''),
          sport: sportFromTicker(ticker),
        };
      });
      setFills(fillRows);

      const bal = acctData?.balance ?? null;
      const rawBal = (bal?.balance ?? bal?.available_balance ?? null) as number | null;
      setBalanceCents(rawBal != null ? Number(rawBal) : null);

      // Honest partial-failure signal so an empty table never reads as "nothing
      // on Kalshi" when really a fetch failed.
      if (!ordRes.ok) setError('Orders did not load — the order list may be incomplete.');
      else if (!fillsRes.ok) setError('Trades did not load — the fill tape may be incomplete.');
      else if (!acctRes.ok) setError('Balance did not load.');
    } catch {
      setError('Could not load org data.');
      setPositions([]);
      setOrders([]);
      setFills([]);
      setBalanceCents(null);
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Side-native current price for a ticker (cents), from the live hydra mark.
  const nowCentsFor = useCallback(
    (ticker: string, side: 'yes' | 'no'): number | null => {
      const yesMid = getMarketPrice(ticker);
      if (yesMid == null) return null;
      return side === 'yes' ? yesMid : 100 - yesMid;
    },
    // marketVersion forces re-eval as prices tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMarketPrice, marketVersion],
  );

  const visiblePositions = useMemo(
    () => positions.filter((p) => sportMatchesFilter(p.sport, sportFilter)),
    [positions, sportFilter],
  );
  const visibleOrders = useMemo(
    () => orders.filter((o) => sportMatchesFilter(o.sport, sportFilter)),
    [orders, sportFilter],
  );
  const visibleFills = useMemo(
    () => fills.filter((f) => sportMatchesFilter(f.sport, sportFilter)),
    [fills, sportFilter],
  );

  // Org aggregates over the visible rows.
  const agg = useMemo(() => {
    let notionalCents = 0;
    let unrealizedCents = 0;
    let unrealizedKnown = false;
    for (const p of visiblePositions) {
      notionalCents += p.costBasisCents;
      const now = nowCentsFor(p.ticker, p.side);
      if (now != null) {
        unrealizedKnown = true;
        unrealizedCents += now * p.qty - p.costBasisCents;
      }
    }
    return { notionalCents, unrealizedCents, unrealizedKnown };
  }, [visiblePositions, nowCentsFor]);

  const masked = !showPnl;

  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex gap-6">
        {/* Main column — full width */}
        <div className="min-w-0 flex-1 space-y-5">
          {/* Sticky toolbar — same flush-pin pattern as the Positions page. */}
          <div className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 -mt-6 border-b px-6 pb-3 pt-6 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
              <Building2 size={20} className="text-muted-foreground" />
              <h1 className="text-xl font-semibold">{orgName ?? 'Organization'}</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter by sport">
                <SportFilterChip
                  mode={sportFilter.size === 0 ? 'include' : 'off'}
                  label="All sports"
                  onClick={() => setSportFilter(new Map())}
                />
                {SPORT_TABS.map((s) => (
                  <SportFilterChip
                    key={s.id}
                    mode={sportFilter.get(s.id) ?? 'off'}
                    label={s.label}
                    onClick={() => setSportFilter((prev) => cycleSportFilter(prev, s.id))}
                  />
                ))}
              </div>
              <button
                onClick={fetchAll}
                disabled={loading}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
          )}

          {/* Summary tiles */}
          <SummaryStrip>
            <SummaryTile label="Org Balance" value={balanceCents != null ? fmtUsd(balanceCents / 100) : '—'} />
            <SummaryTile label="Open Positions" value={String(visiblePositions.length)} />
            <SummaryTile label="Notional" value={fmtUsd(agg.notionalCents / 100)} />
            <SummaryTile label="Open Orders" value={String(visibleOrders.length)} />
            <SummaryTile label="Trades" value={String(visibleFills.length)} />
            <SummaryTile
              label="Unrealized P&L"
              value={masked ? '•••' : agg.unrealizedKnown ? fmtUsdSigned(agg.unrealizedCents / 100) : '—'}
              tone={masked || !agg.unrealizedKnown ? 'default' : agg.unrealizedCents >= 0 ? 'positive' : 'danger'}
            />
          </SummaryStrip>

          {/* Org positions — attributed. Same view-mode choices as /positions
              (shared positionViewMode); the table keeps the full attributed
              column layout, cards/compact carry the owner via `attribution`. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-foreground text-sm font-medium">
                Open Positions <span className="text-muted-foreground">· {visiblePositions.length}</span>
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleShowPnl}
                  className="text-muted-foreground hover:text-foreground rounded p-1"
                  title={showPnl ? 'Hide P&L' : 'Show P&L'}
                >
                  {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                {visiblePositions.length > 0 && (
                  <ViewModeToggle
                    value={positionViewMode}
                    modes={POSITION_VIEW_MODES}
                    onChange={setPositionViewMode}
                  />
                )}
              </div>
            </div>
            {loading ? (
              <div className="bg-card border-border flex justify-center rounded-lg border py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : visiblePositions.length === 0 ? (
              <div className="bg-card border-border rounded-lg border">
                <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open positions</p>
              </div>
            ) : positionViewMode === 'card' || positionViewMode === 'mini' ? (
              <CardGrid variant={positionViewMode === 'mini' ? 'mini' : 'default'}>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(p, nowCentsFor(p.ticker, p.side));
                  return (
                    <PositionCard
                      key={vm.key}
                      vm={vm}
                      hidePnl={!showPnl}
                      mini={positionViewMode === 'mini'}
                      onEdit={() => {}}
                      attribution={<OwnerTag ownerKind={p.ownerKind} label={p.ownerLabel} />}
                    />
                  );
                })}
              </CardGrid>
            ) : positionViewMode === 'compact' ? (
              <CompactListGrid>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(p, nowCentsFor(p.ticker, p.side));
                  return (
                    <CompactPositionItem
                      key={vm.key}
                      vm={vm}
                      hidePnl={!showPnl}
                      onEdit={() => {}}
                      attribution={<OwnerTag ownerKind={p.ownerKind} label={p.ownerLabel} />}
                    />
                  );
                })}
              </CompactListGrid>
            ) : (
              <div className="bg-card border-border rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Now</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-right">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePositions.map((p, i) => {
                    const { marketLabel, betTeam } = describeMarket(p.ticker, p.side);
                    const now = nowCentsFor(p.ticker, p.side);
                    const pnlCents = now != null ? now * p.qty - p.costBasisCents : null;
                    return (
                      <tr key={`${p.userId}-${p.ticker}-${p.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><OwnerTag ownerKind={p.ownerKind} label={p.ownerLabel} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={p.side} betTeam={betTeam} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(p.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">{p.avgCents}¢</td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {now != null ? `${now}¢` : '—'}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {fmtUsd(p.costBasisCents / 100)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs ${
                            masked || pnlCents == null
                              ? 'text-muted-foreground'
                              : pnlCents >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {masked ? '•••' : pnlCents != null ? fmtUsdSigned(pnlCents / 100) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Org orders — attributed */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Open Orders <span className="text-muted-foreground">· {visibleOrders.length}</span>
              </h2>
            </div>
            {visibleOrders.length === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open orders</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((o, i) => {
                    const { marketLabel, betTeam } = describeMarket(o.ticker, o.side);
                    return (
                      <tr key={o.orderId || `${o.ticker}-${o.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><OwnerTag ownerKind={o.ownerKind} label={o.ownerLabel} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={o.side} betTeam={betTeam} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(o.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {o.priceCents != null ? `${o.priceCents}¢` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Org trades/fills — owner-tagged, including unattributed rows. */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Trades <span className="text-muted-foreground">· {visibleFills.length}</span>
              </h2>
            </div>
            {visibleFills.length === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">No trades</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-center">Action</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Fee</th>
                    <th className="px-4 py-2 text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFills.slice(0, 80).map((f, i) => {
                    const { marketLabel, betTeam } = describeMarket(f.ticker, f.side);
                    const when = f.executedAt ? new Date(f.executedAt) : null;
                    const timeLabel = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '—';
                    return (
                      <tr key={f.fillId || `${f.ticker}-${f.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><OwnerTag ownerKind={f.ownerKind} label={f.ownerLabel} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={f.side} betTeam={betTeam} /></td>
                        <td className="px-3 py-2 text-center">
                          <span className={f.action === 'buy' ? 'text-emerald-400' : 'text-amber-400'}>
                            {f.action.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(f.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.priceCents != null ? `${f.priceCents}¢` : '—'}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.feeCents != null ? fmtUsd(f.feeCents / 100) : '—'}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 text-right text-[11px]">{timeLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right sidebar — compact per-trader rollup (the "who"), built to fit
            the narrow rail (the full OrgTradersTable's 6 columns overflowed it). */}
        <aside className="sticky top-6 hidden w-72 shrink-0 self-start lg:block">
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-3 py-2.5 text-sm font-medium">
              Members <span className="text-muted-foreground">· {members.length}</span>
            </div>
            <div className="max-h-[calc(100vh-9rem)] overflow-y-auto">
              {members.length === 0 ? (
                <p className="text-muted-foreground px-3 py-6 text-center text-sm">No members</p>
              ) : (
                members.map((m) => {
                  const memberUnreal = (m.openPositions ?? []).reduce((sum, p) => {
                    const now = nowCentsFor(p.ticker, p.side);
                    return now != null
                      ? sum + (now * p.netCount - (p.costBasisCents ?? p.avgEntryPriceCents * p.netCount))
                      : sum;
                  }, 0);
                  const memberPnl = m.realizedPnlCents + memberUnreal;
                  return (
                    <button
                      key={m.userId}
                      onClick={() => onTraderClick(m)}
                      className="border-border/40 hover:bg-muted/40 w-full border-b px-3 py-2 text-left transition-colors last:border-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground truncate text-xs font-medium">{m.username}</span>
                        <span
                          className={`shrink-0 font-mono text-xs ${
                            masked ? 'text-muted-foreground' : memberPnl >= 0 ? 'text-positive' : 'text-negative'
                          }`}
                        >
                          {masked ? '•••' : fmtUsdSigned(memberPnl / 100)}
                        </span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 text-[11px]">
                        {m.openCount} ctr · {m.openOrderCount ?? 0} ord · {fmtUsd(m.openNotionalCents / 100)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
